import { useState } from "react";
import { getStorageItem, setStorageItem } from "@tellescope/utils";
import type { PopupBlock as PopupBlockType } from "../../../shared/schema";
import { PERIOD_DURATIONS } from "../../../shared/schema";
import { usePortalSchemaContext } from "../../../shared/context/PortalSchemaContext";
import { PopupDialog } from "../../../shared/components/PopupDialog";

interface PopupBlockProps {
  block: PopupBlockType;
  isGlobalRender?: boolean; // True when rendered by PortalContent for global popups
}

const getStorageKey = (blockId: string) => `portal-popup-dismissed-${blockId}`;

const shouldShowPopup = (
  blockId: string,
  behavior: PopupBlockType["props"]["dismissalBehavior"],
  period?: PopupBlockType["props"]["dismissalPeriod"]
): boolean => {
  if (behavior === "always") return true;

  const stored = getStorageItem<{ dismissedAt: number } | null>(getStorageKey(blockId), null);
  if (!stored) return true;

  if (behavior === "forever") return false;

  if (behavior === "period" && period) {
    const elapsed = Date.now() - stored.dismissedAt;
    return elapsed >= PERIOD_DURATIONS[period];
  }

  return true;
};

export const PopupBlock = ({ block, isGlobalRender = false }: PopupBlockProps) => {
  const previewSchema = usePortalSchemaContext();
  const isAdminPreview = previewSchema !== null;

  const {
    content,
    variant,
    customBackgroundColor,
    customTextColor,
    title,
    size,
    dismissalBehavior,
    dismissalPeriod,
    showOnAllPages,
  } = block.props;

  // Skip render in BlockRenderer if it's a global popup (will be rendered by PortalContent)
  if (showOnAllPages && !isGlobalRender) return null;

  // In admin preview, don't auto-show - use the editor's preview button instead
  if (isAdminPreview) return null;

  const [isOpen, setIsOpen] = useState(() => shouldShowPopup(block.id, dismissalBehavior, dismissalPeriod));

  const handleClose = () => {
    setIsOpen(false);
    if (dismissalBehavior === "forever" || dismissalBehavior === "period") {
      setStorageItem(getStorageKey(block.id), { dismissedAt: Date.now() });
    }
  };

  if (!content || !isOpen) return null;

  return (
    <PopupDialog
      open={isOpen}
      onClose={handleClose}
      content={content}
      variant={variant}
      size={size}
      title={title}
      customBackgroundColor={customBackgroundColor}
      customTextColor={customTextColor}
    />
  );
};
